import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Gift, Trash2, Wallet, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PagarDialog, sendRecibo } from "@/components/PagamentoDialogs";
import { PagamentosList } from "@/components/PagamentosList";
import { RelatorioComissoes } from "@/components/RelatorioComissoes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/PageHeader";
import { Field, BigButton } from "@/components/Field";
import { Panel, SelectField, Empty, inputCls } from "@/components/Bits";
import { useFetch, fmtDate, BRL } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

export const listTabs = "h-14 p-1 rounded-xl bg-white border border-[#E2DDD3] mb-5 w-full sm:w-auto overflow-x-auto justify-start";
export const tabCls = "h-12 px-4 rounded-lg flex-1 data-[state=active]:bg-[#1E4D2B] data-[state=active]:text-white";

export const Totais = ({ d }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
    {[["A receber", d.a_receber, "bg-[#1E4D2B] text-white", "com-a-receber"], ["Recebido", d.recebido, "bg-white", "com-recebido"], ["Previsto (OS em aberto)", d.previsto, "bg-white", "com-previsto"]].map(([l, v, c, t]) => (
      <div key={l} className={`rounded-2xl border border-[#E2DDD3] p-5 ${c}`} data-testid={t}>
        <p className="text-xs font-bold uppercase tracking-wider opacity-80">{l}</p>
        <p className="font-display text-3xl font-extrabold mt-1">{BRL(v)}</p>
      </div>
    ))}
  </div>
);

const PayBadge = ({ paga, testId }) => (
  <span data-testid={testId} className={`h-11 px-3 rounded-xl text-sm font-semibold flex items-center gap-1 shrink-0 ${paga ? "bg-[#E8F5E9] text-[#1E4D2B]" : "bg-[#FEF3C7] text-[#92400E]"}`}>
    {paga && <Check className="h-4 w-4" />}{paga ? "Paga" : "Pendente"}
  </span>
);

const toRows = (d, isAdmin) => [
  ...d.itens.map((i) => ({ k: i.os_id, date: i.finalizada_em, titulo: <Link to={`/ordens-servico/${i.os_id}`} className="font-semibold hover:underline">{i.numero} · {i.categoria_nome || i.servico_solicitado}</Link>, sub: `${i.cliente_nome || ""}${isAdmin ? ` · ${i.tecnico_nome}` : ""}`, valor: i.comissao.valor, paga: i.comissao.paga })),
  ...d.bonus.map((b) => ({ k: b.bonus_id, date: b.data, bonus: b, titulo: <span className="font-semibold flex items-center gap-1"><Gift className="h-4 w-4 text-[#C86D51]" /> Bônus · {b.descricao}</span>, sub: isAdmin ? b.tecnico_nome : "", valor: b.valor, paga: b.paga })),
].sort((a, b) => String(b.date).localeCompare(String(a.date)));

export const Lista = ({ d, isAdmin, canDelBonus, reload, tec, onPaid }) => {
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(false);
  const delBonus = async (b) => { try { await api.delete(`/bonus/${b.bonus_id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const rows = toRows(d, isAdmin);
  const selectable = isAdmin && !!tec;
  const pend = rows.filter((r) => !r.paga);
  const chosen = rows.filter((r) => sel.includes(r.k));
  const flip = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  if (!rows.length) return <Empty testId="com-empty">Nenhuma comissão ou bônus ainda.</Empty>;
  return (
    <div className="space-y-2" data-testid="com-list">
      {isAdmin && !tec && pend.length > 0 && <p className="text-sm text-[#5C6B66] mb-2" data-testid="com-select-hint">Selecione um técnico para registrar pagamentos.</p>}
      {selectable && pend.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          <button onClick={() => setSel(sel.length === pend.length ? [] : pend.map((r) => r.k))} className="h-12 px-4 rounded-xl border-2 border-[#E2DDD3] font-semibold" data-testid="com-select-all">
            {sel.length === pend.length ? "Limpar seleção" : "Selecionar pendentes"}</button>
          <button disabled={!chosen.length} onClick={() => setOpen(true)} className="h-12 px-4 rounded-xl bg-[#1E4D2B] text-white font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="com-pagar-button">
            <Wallet className="h-5 w-5" /> Registrar pagamento ({BRL(chosen.reduce((a, r) => a + r.valor, 0))})</button>
        </div>
      )}
      {rows.map((r) => (
        <div key={r.k} className="bg-white rounded-2xl border border-[#E2DDD3] p-4 flex items-center gap-3" data-testid={`com-item-${r.k}`}>
          {selectable && !r.paga && <input type="checkbox" checked={sel.includes(r.k)} onChange={() => flip(r.k)} className="h-6 w-6 accent-[#1E4D2B] shrink-0" data-testid={`com-check-${r.k}`} />}
          <div className="min-w-0 flex-1">{r.titulo}<p className="text-xs text-[#5C6B66] truncate">{fmtDate(r.date)} {r.sub && `· ${r.sub}`}</p></div>
          <p className="font-bold text-[#1E4D2B] shrink-0">{BRL(r.valor)}</p>
          <PayBadge paga={r.paga} testId={`com-pay-${r.k}`} />
          {canDelBonus && r.bonus && !r.paga && <button onClick={() => delBonus(r.bonus)} aria-label="Excluir bônus" className="h-11 w-11 rounded-xl border border-[#E2DDD3] flex items-center justify-center text-[#B05A40]" data-testid={`bonus-delete-${r.k}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      <PagarDialog open={open} onOpenChange={setOpen} tecnicoId={tec} rows={chosen} onSaved={(p) => { setSel([]); reload(); onPaid(p); }} />
    </div>
  );
};

export const PagoDialog = ({ pag, onClose }) => (
  <Dialog open={!!pag} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md rounded-3xl" data-testid="pago-dialog">
      <DialogHeader><DialogTitle className="font-display text-2xl">Recibo {pag?.numero} gerado</DialogTitle></DialogHeader>
      <p className="-mt-2 text-[#5C6B66]">Envie o recibo de {BRL(pag?.valor)} para {pag?.tecnico_nome} assinar pelo WhatsApp.</p>
      <button onClick={() => { sendRecibo(pag.pagamento_id); onClose(); }} className="h-14 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2" data-testid="pago-whatsapp"><MessageCircle className="h-5 w-5" /> Enviar recibo no WhatsApp</button>
      <button onClick={onClose} className="h-12 text-[#5C6B66] font-semibold" data-testid="pago-skip">Depois</button>
    </DialogContent>
  </Dialog>
);

export const Valores = () => {
  const { data, reload } = useFetch("/categorias-servico");
  const save = async (c, v) => {
    try { await api.put(`/categorias-servico/${c.categoria_id}`, { nome: c.nome, ativo: c.ativo, comissao: Number(v) || 0 }); toast.success("Comissão salva"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Valor da comissão por serviço" testId="com-valores">
      <div className="divide-y divide-[#E2DDD3]">
        {(data || []).map((c) => (
          <div key={c.categoria_id} className="py-3 flex items-center gap-3">
            <span className={`flex-1 min-w-0 ${c.ativo ? "" : "text-[#5C6B66] line-through"}`}>{c.nome}</span>
            <span className="text-sm text-[#5C6B66]">R$</span>
            <input type="number" min="0" step="0.01" defaultValue={c.comissao || 0} onBlur={(e) => Number(e.target.value) !== (c.comissao || 0) && save(c, e.target.value)}
              className={`${inputCls} w-32 h-12`} data-testid={`com-valor-${c.categoria_id}`} />
          </div>
        ))}
      </div>
      <p className="text-xs text-[#5C6B66] mt-3">O valor é aplicado ao técnico responsável quando a OS é encerrada.</p>
    </Panel>
  );
};

export const BonusForm = ({ equipe, onSaved }) => {
  const [f, setF] = useState({ tecnico_id: "", valor: "", descricao: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/bonus", { ...f, valor: Number(f.valor) }); toast.success("Bônus adicionado"); setF({ tecnico_id: "", valor: "", descricao: "" }); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Panel title="Adicionar bônus individual" testId="bonus-form">
      <form onSubmit={submit} className="grid sm:grid-cols-[1fr_140px] gap-3">
        <SelectField label="Técnico *" id="b-tec" required placeholder="Selecione" value={f.tecnico_id} onChange={(e) => setF({ ...f, tecnico_id: e.target.value })}
          options={(equipe || []).map((u) => ({ value: u.user_id, label: u.name }))} data-testid="bonus-tecnico" />
        <Field label="Valor (R$) *" id="b-val" type="number" min="0.01" step="0.01" required value={f.valor} onChange={(e) => setF({ ...f, valor: e.target.value })} data-testid="bonus-valor" />
        <div className="sm:col-span-2"><Field label="Motivo *" id="b-desc" required minLength={2} value={f.descricao} onChange={(e) => setF({ ...f, descricao: e.target.value })} data-testid="bonus-descricao" /></div>
        <div className="sm:col-span-2"><BigButton type="submit" variant="earth" data-testid="bonus-submit"><Gift className="h-5 w-5" /> Adicionar bônus</BigButton></div>
      </form>
    </Panel>
  );
};

export default function ComissoesPage() {
  const [params] = useSearchParams();
  const { data, reload } = useFetch("/comissoes");
  if (!data) return null;
  return (
    <div data-testid="comissoes-page">
      <PageHeader eyebrow="Financeiro" title="Minhas comissões" subtitle="Comissões das OS encerradas, bônus e recibos de pagamento." />
      <Totais d={data} />
      <Tabs defaultValue={params.get("recibo") ? "recibos" : "itens"}>
        <TabsList className={listTabs}>
          <TabsTrigger value="itens" className={tabCls} data-testid="tab-itens">Comissões e bônus</TabsTrigger>
          <TabsTrigger value="recibos" className={tabCls} data-testid="tab-recibos">Recibos</TabsTrigger>
        </TabsList>
        <TabsContent value="itens"><Lista d={data} reload={reload} /></TabsContent>
        <TabsContent value="recibos"><PagamentosList onChange={reload} /></TabsContent>
      </Tabs>
    </div>
  );
}
